import { Grow } from "@mui/material";
import * as React from "react";
import Localization from "../core/Localization";
import Button from "../coreui/Button";
import Icon from "../coreui/Icon";
import { TableChildProps } from "../coreui/Table";
import { FilterContext } from "../coreui/table/FilterContext";
import TextField from "../coreui/TextField";
import { ToolbarInputWrapper } from "./ToolbarInputWrapper";

const GridFilter: React.FC<{
  propagated: TableChildProps;
}> = (props): JSX.Element => {
  const { filterValue, setFilterValue } = React.useContext(FilterContext);
  const [inputFieldFocused, setInputFieldFocused] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const inputElementRef = React.useRef<HTMLInputElement>(null);

  const depth: number = props.propagated.parentTable.cardDepth;
  const filterVisible = filterValue !== null;

  const close = (): void => {
    setFilterValue(null);
    setInputFieldFocused(false);
  };

  const onButtonClick = () => {
    if (filterValue === null) {
      setFilterValue("");
    } else {
      close();
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.currentTarget.value;

    setFilterValue(value);
  };

  const onGrowEnd = (node: HTMLElement): void => {
    inputElementRef.current!.focus();
  };

  const onGrowStart = (node: HTMLElement, isAppearing?: boolean): void => {
    node.style.visibility = "visible";
    inputElementRef.current!.focus();
  };

  const onShrinkEnd = (node: HTMLElement): void => {
    node.style.visibility = "hidden";
  };

  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        position: "relative",
      }}
    >
      <Grow
        in={filterVisible}
        onEnter={onGrowStart}
        onEntered={onGrowEnd}
        onExited={onShrinkEnd}
        style={{ visibility: "hidden", zIndex: 2 }}
        timeout={500}
      >
        <ToolbarInputWrapper
          inputElement={inputElementRef.current!}
          onClose={() => {
            close();
            if (buttonRef.current) {
              buttonRef.current.focus();
            }
          }}
        >
          <TextField
            disabled={!filterVisible}
            icon="fas fa-filter"
            inputProps={{ spellCheck: false, tabIndex: -1 }}
            inputRef={inputElementRef}
            label={Localization.getBuiltInMessage("filter")}
            onBlur={() => setInputFieldFocused(false)}
            onChange={onChange}
            onFocus={() => setInputFieldFocused(true)}
            style={{
              width: props.propagated.parentTable.isVerticalLayout
                ? `calc(100vw - ${depth * 32 + 64}px)`
                : 272,
            }}
            value={filterValue || ""}
            variant="filled"
          />
        </ToolbarInputWrapper>
      </Grow>
      <Button
        aria-label={
          filterVisible
            ? Localization.getBuiltInMessage("close")
            : Localization.getBuiltInMessage("filter")
        }
        fab
        onClick={onButtonClick}
        ref={buttonRef}
        size="small"
        tabIndex={inputFieldFocused ? 0 : -1}
      >
        <Icon
          icon={filterVisible ? "fas fa-times" : "fas fa-filter"}
          style={{ marginTop: 2, width: "100%" }}
        />
      </Button>
    </div>
  );
};

export default GridFilter;
